// 365 Embed loader — the single public entry point a venue site includes:
//   <script src="https://embed.365sports.online/v1/loader.js" data-venue-key="pub_live_xxx" async></script>
//   <div data-365="booking"></div>  <div data-365="watch"></div>
//
// Reads the venue key + theming from its own script tag, fetches the venue config, then renders any
// known [data-365] mount in an isolated shadow root. Unknown widget names are ignored — so a future
// widget needs no change to existing sites (just a new mount + an updated SDK).
import type { EmbedConfig, ThemeMode, ThemeOptions } from './types';
import { SessionStore } from './store';
import { Auth } from './auth';
import { Api } from './api';
import { mountBooking, mountMyBookings } from './widgets/booking';
import { mountWatch, mountMyStreams } from './widgets/watch';
import { mountMembership, mountMembershipCompare, mountMyMembership } from './widgets/membership';
import { mountMemberCard } from './widgets/member-card';
import { mountMyQr } from './widgets/my-qr';
import { mountAdvertise } from './widgets/advertise';
import { mountMyAds } from './widgets/my-ads';
import { mountNotificationSettings } from './widgets/account';
import { mountConsent } from './widgets/consent';
import { mountJobs } from './widgets/jobs';
import { mountRefundPolicy } from './widgets/refund-policy';
import { mountPolicies } from './widgets/policies';
import { handleReturn } from './return';
import { initPages } from './pages';
import { configureMaintenance, isMaintenanceResponse, probeMaintenance, showMaintenance } from './maintenance';

declare const __API_BASE__: string; // baked at build time (sandbox vs prod)

interface ScriptOptions {
  venueKey: string;
  apiBase: string;
  theme: ThemeOptions;
}

function readScript(): ScriptOptions | null {
  const el =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector('script[data-venue-key]');
  if (!el) return null;
  const venueKey = el.getAttribute('data-venue-key') || '';
  if (!venueKey) return null;
  initPages(el.getAttribute('data-365-pages')); // optional site route map (defaults = scaffold routes)
  const themeAttr = (el.getAttribute('data-365-theme') || '').toLowerCase();
  const mode: ThemeMode | undefined = themeAttr === 'dark' || themeAttr === 'light' ? themeAttr : undefined;
  return {
    venueKey,
    apiBase: (el.getAttribute('data-api-base') || __API_BASE__).replace(/\/+$/, ''),
    theme: {
      mode,
      primary: el.getAttribute('data-365-primary') || undefined,
      primaryContrast: el.getAttribute('data-365-primary-contrast') || undefined,
      radius: el.getAttribute('data-365-radius') || undefined,
      font: el.getAttribute('data-365-font') || undefined,
      bg: el.getAttribute('data-365-bg') || undefined,
      text: el.getAttribute('data-365-text') || undefined,
      border: el.getAttribute('data-365-border') || undefined,
      muted: el.getAttribute('data-365-muted') || undefined,
      surface: el.getAttribute('data-365-surface') || undefined,
    },
  };
}

async function boot(): Promise<void> {
  const opts = readScript();
  if (!opts) {
    console.warn('[365-embed] missing data-venue-key on the loader script tag');
    return;
  }

  // Maintenance: if the API is mid-deploy (503 maintenance JSON, or no answer at all) every mount gets
  // one "We're updating" card that polls api/status and reloads when the API is back — instead of the
  // venue's page silently showing nothing where the booking widget should be.
  configureMaintenance(opts.apiBase, opts.theme);

  let cfg: EmbedConfig;
  try {
    const res = await fetch(`${opts.apiBase}/api/embed/${encodeURIComponent(opts.venueKey)}/config`);
    if (res.status === 503) {
      const info = await isMaintenanceResponse(res);
      if (info) { showMaintenance(info); return; }
    }
    if (!res.ok) {
      console.warn(`[365-embed] config ${res.status} — widgets not rendered`);
      return;
    }
    cfg = (await res.json()) as EmbedConfig;
  } catch (e) {
    const info = navigator.onLine ? await probeMaintenance(opts.apiBase) : null;
    if (info) { showMaintenance(info); return; }
    console.warn('[365-embed] failed to load config', e);
    return;
  }
  cfg.baseApiUrl = (cfg.baseApiUrl || opts.apiBase).replace(/\/+$/, '');

  const store = new SessionStore(opts.venueKey);
  const auth = new Auth(cfg, opts.venueKey, store);
  const api = new Api(cfg, auth);

  // Expose a tiny public API so the host site's own chrome (e.g. an "Account" menu) can reflect login
  // state and sign the customer out — the widgets carry their own in-shadow-DOM "Sign out", but a header
  // control is more discoverable. logout() clears the token and reloads so every widget re-renders logged-out.
  (window as unknown as { Embed365?: unknown }).Embed365 = {
    isLoggedIn: (): boolean => auth.isLoggedIn,
    email: (): string | null => auth.profile?.email ?? null,
    login: (): void => auth.redirectToLogin(),
    logout: (): void => { auth.logout(); try { location.reload(); } catch { /* ignore */ } },
  };
  try { window.dispatchEvent(new CustomEvent('embed365:ready', { detail: { isLoggedIn: auth.isLoggedIn } })); } catch { /* ignore */ }

  // If we just returned from the redirect login (?365code=), swap it for a token before mounting so the
  // widget renders logged-in and can resume the action the user clicked pre-login.
  await auth.completeReturn();

  // Return pages embed the same loader; if this is a Stripe return, handle it and skip normal mount.
  if (await handleReturn(api, cfg, opts.theme)) return;

  const mounts = Array.from(document.querySelectorAll('[data-365]')) as HTMLElement[];
  for (const mount of mounts) {
    const widget = mount.getAttribute('data-365');
    try {
      if (widget === 'booking') mountBooking(mount, cfg, api, auth, opts.theme);
      else if (widget === 'watch') mountWatch(mount, cfg, api, auth, opts.theme, store);
      else if (widget === 'my-streams') mountMyStreams(mount, cfg, api, auth, opts.theme);
      else if (widget === 'my-bookings') mountMyBookings(mount, cfg, api, auth, opts.theme);
      else if (widget === 'membership') mountMembership(mount, cfg, api, auth, opts.theme);
      else if (widget === 'membership-compare') mountMembershipCompare(mount, cfg, api, auth, opts.theme);
      else if (widget === 'my-membership') mountMyMembership(mount, cfg, api, auth, opts.theme);
      else if (widget === 'member-card') mountMemberCard(mount, cfg, api, auth, opts.theme);
      else if (widget === 'my-qr') mountMyQr(mount, cfg, api, auth, opts.theme);
      else if (widget === 'advertise') mountAdvertise(mount, cfg, api, auth, opts.theme);
      else if (widget === 'my-ads') mountMyAds(mount, cfg, api, auth, opts.theme);
      else if (widget === 'notification-settings') mountNotificationSettings(mount, cfg, api, auth, opts.theme);
      else if (widget === 'consent') mountConsent(mount, cfg, api, auth, opts.theme);
      else if (widget === 'jobs') mountJobs(mount, cfg, opts.theme, opts.venueKey);
      else if (widget === 'refund-policy') mountRefundPolicy(mount, cfg, opts.theme, opts.venueKey);
      else if (widget === 'policies') mountPolicies(mount, cfg, opts.theme, opts.venueKey);
      // unknown data-365 values are intentionally ignored (forward-compatible).
    } catch (e) {
      console.warn(`[365-embed] failed to mount "${widget}"`, e);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot());
} else {
  void boot();
}
